import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  SecurityContext,
  ViewChild
} from '@angular/core'
import { DomSanitizer } from '@angular/platform-browser'
import { JwtHelperService } from '@auth0/angular-jwt'
import { CookieService } from 'ngx-cookie-service'

import {
  AkerChatMessageRecord,
  AkerChatRecord,
  AkerMessage,
  AkerMessageUsage,
  AkerService,
  AkerStreamFile
} from 'app/services/aker.service'

interface AkerChatSummary {
  id: string
  backendChatId?: number
  externalChatId?: string
  isPersisting?: boolean
  title: string
  createdAt: Date
  sessionId: string
  messages: AkerMessage[]
}

@Component({
  selector: 'app-aker',
  templateUrl: './aker.component.html',
  styleUrls: ['./aker.component.scss']
})
export class AkerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chatMessages') chatMessages?: ElementRef<HTMLDivElement>
  @ViewChild('chatTextarea') chatTextarea?: ElementRef<HTMLTextAreaElement>

  private readonly heroMessages = [
    'Que vamos a hacer hoy?',
    'En que te puedo ayudar?',
    'Listo para empezar cuando quieras.',
    'Dime que necesitas y lo vemos juntos.',
    'Que quieres analizar primero?',
    'Estoy aqui para ayudarte con lo que necesites.',
    'Empezamos por ventas, productos o establecimientos?',
    'Cuentame tu objetivo y arrancamos.',
    'Que consulta quieres resolver ahora?',
    'Cuando quieras, lanzamos la primera pregunta.'
  ]

  private readonly suggestionPool = [
    'Cuales son las ventas del ultimo mes',
    'Inventario por producto',
    'Tiendas con mayor cantidad de ventas',
    'Margenes de beneficio por tienda',
    'Ordenes de compra de la ultima semana',
    'Listado de productos',
    'Listado de tiendas operativas',
    'Cuales son los productos mas comprados',
    'Que productos tienen mayor rentabilidad',
    'Stock de productos por tienda',
    'Valorizacion de inventario'
  ]

  heroTitle = ''
  prompt = ''
  sessionId = ''
  isSending = false
  assistantStatus = ''
  userName = this.getUserName()
  visibleSuggestions = this.getRandomSuggestions()
  messages: AkerMessage[] = []
  currentChatId = this.createChatId()
  chatHistory: AkerChatSummary[] = []
  isChatSidebarCollapsed = false
  isLoadingChatMessages = false
  openedChatMenuId = ''
  openedChatMenuTop = 0
  openedChatMenuLeft = 0
  deletingChatId = ''

  private streamController?: AbortController
  private waitTimer?: ReturnType<typeof setInterval>
  private chatLoadRequestId = 0
  private currentUserMessage?: AkerMessage
  private currentAssistantMessage?: AkerMessage
  private exitCancellationRequested = false

  constructor(
    private akerService: AkerService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private cookies: CookieService
  ) {
    this.heroTitle = this.getRandomHeroMessage()
  }

  ngOnInit(): void {
    this.loadChatHistory()
  }

  ngAfterViewInit(): void {
    this.resizeTextarea()
    this.scrollToBottom()
    this.focusPrompt()
  }

  ngOnDestroy(): void {
    this.cancelActiveSessionOnExit()
    this.streamController?.abort()
    this.stopWaitTimer()
    this.revokeAttachmentUrls()
  }

  @HostListener('window:pagehide')
  onPageHide() {
    this.cancelActiveSessionOnExit()
  }

  @HostListener('window:pageshow')
  onPageShow() {
    this.exitCancellationRequested = false
  }

  useSuggestion(suggestion: string) {
    if (this.isSending || this.isLoadingChatMessages) {
      return
    }

    this.prompt = suggestion
    this.resizeTextarea()
    this.sendMessage()
  }

  toggleChatSidebar() {
    this.isChatSidebarCollapsed = !this.isChatSidebarCollapsed
    this.resizeTextarea()
    this.scrollToBottom()
  }

  async startNewChat() {
    if (this.isSending) {
      await this.cancelActiveRequest(false)
    }

    this.resetCurrentChat()
    this.resizeTextarea()
  }

  selectChat(chat: AkerChatSummary) {
    if (this.isSending) {
      return
    }

    this.openedChatMenuId = ''
    this.currentChatId = chat.id
    this.sessionId = chat.sessionId
    this.messages = chat.messages
    this.prompt = ''
    this.assistantStatus = ''
    this.isLoadingChatMessages = false
    this.resizeTextarea()
    this.scrollToBottom()
    this.resolveChatMessages(chat)
  }

  toggleChatMenu(chat: AkerChatSummary, event: MouseEvent) {
    event.stopPropagation()
    const target = event.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    const menuWidth = 118

    if (this.openedChatMenuId === chat.id) {
      this.openedChatMenuId = ''
      return
    }

    this.openedChatMenuId = chat.id
    this.openedChatMenuTop = rect.bottom + 6
    this.openedChatMenuLeft = Math.max(8, rect.right - menuWidth)
  }

  getOpenedChatMenu() {
    return this.chatHistory.find((chat) => chat.id === this.openedChatMenuId)
  }

  deleteChat(chat: AkerChatSummary | undefined, event: MouseEvent) {
    event.stopPropagation()

    if (!chat || this.isSending || this.deletingChatId) {
      return
    }

    this.openedChatMenuId = ''
    this.deletingChatId = chat.id

    this.akerService
      .deleteChat(chat.backendChatId, chat.externalChatId || chat.sessionId)
      .then(() => {
        this.chatHistory = this.chatHistory.filter(
          (item) => item.id !== chat.id
        )

        if (this.currentChatId === chat.id) {
          this.resetCurrentChat()
        }
      })
      .catch(() => {
        this.assistantStatus = 'No se pudo eliminar el chat.'
      })
      .finally(() => {
        if (this.deletingChatId === chat.id) {
          this.deletingChatId = ''
        }
      })
  }

  sendMessage() {
    const content = this.prompt.trim()

    if (!content || this.isSending || this.isLoadingChatMessages) {
      return
    }

    this.ensureChatHistory(content)

    const userMessage: AkerMessage = {
      id: this.createMessageId(),
      role: 'user',
      content,
      createdAt: new Date()
    }

    const assistantMessage: AkerMessage = {
      id: this.createMessageId(),
      role: 'assistant',
      content: '',
      createdAt: new Date(),
      isStreaming: true,
      waitStartedAt: Date.now(),
      elapsedSeconds: 0
    }
    const conversation = [...this.messages, userMessage].map(
      ({ role, content: messageContent }) => ({
        role,
        content: messageContent
      })
    )

    this.messages = [...this.messages, userMessage, assistantMessage]
    this.updateCurrentChatMessages()
    this.prompt = ''
    this.isSending = true
    this.assistantStatus = ''
    this.currentUserMessage = userMessage
    this.currentAssistantMessage = assistantMessage
    this.startWaitTimer(assistantMessage)
    this.resizeTextarea()
    this.scrollToBottom()

    this.streamController = this.akerService.streamMessage(
      {
        message: content,
        sessionId: this.sessionId,
        conversation
      },
      {
        onSession: (nextSessionId) => {
          this.sessionId = nextSessionId
          this.updateCurrentChatSession(nextSessionId)
          this.persistCurrentChatWhenSessionIsReady()
          this.cdr.detectChanges()
        },
        onChunk: (chunk) => {
          assistantMessage.content += chunk
          this.messages = [...this.messages]
          this.updateCurrentChatMessages()
          this.cdr.detectChanges()
          this.scrollToBottom()
        },
        onFile: (file) => {
          const attachment = this.createAttachment(file)
          const hasSameAttachment = assistantMessage.attachments?.some(
            (currentAttachment) =>
              currentAttachment.name === attachment.name &&
              currentAttachment.size === attachment.size
          )

          if (hasSameAttachment) {
            URL.revokeObjectURL(attachment.objectUrl)
            return
          }

          assistantMessage.attachments = [
            ...(assistantMessage.attachments || []),
            attachment
          ]
          this.messages = [...this.messages]
          this.updateCurrentChatMessages()
          this.cdr.detectChanges()
          this.scrollToBottom()
        },
        onDone: (payload) => {
          const nextSessionId = payload?.sessionId
          const usage = payload?.usage

          if (nextSessionId) {
            this.sessionId = nextSessionId
            this.updateCurrentChatSession(nextSessionId)
            this.persistCurrentChatWhenSessionIsReady()
          }

          userMessage.usage = usage
          this.finishStreamingMessage(assistantMessage, undefined, usage)
        },
        onError: (error) => {
          this.sessionId = ''
          this.updateCurrentChatSession('')
          this.finishStreamingMessage(
            assistantMessage,
            this.getFriendlyErrorMessage(error) ||
              'No se pudo completar la respuesta de AKER.'
          )
        }
      }
    )
  }

  onPromptKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  onPromptInput() {
    this.resizeTextarea()
  }

  getMessageHtml(content: string) {
    const rendered = this.renderMarkdown(content)

    return this.sanitizer.sanitize(SecurityContext.HTML, rendered) || ''
  }

  trackByMessage(_index: number, message: AkerMessage) {
    return message.id
  }

  trackByChat(_index: number, chat: AkerChatSummary) {
    return chat.id
  }

  formatElapsedTime(seconds = 0) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  get hasMessages() {
    return this.messages.length > 0
  }

  get isComposerLocked() {
    return this.isSending || this.isLoadingChatMessages
  }

  cancelActiveRequest(showStatus = true): Promise<void> {
    if (!this.isSending || !this.currentAssistantMessage) {
      return Promise.resolve()
    }

    const assistantMessage = this.currentAssistantMessage
    const activeSessionId = this.sessionId

    if (showStatus) {
      this.assistantStatus = 'Cancelando solicitud en curso...'
    }

    this.streamController?.abort()
    this.streamController = undefined

    return this.akerService
      .cancelSession(activeSessionId)
      .catch(() => {
        this.sessionId = ''
        this.updateCurrentChatSession('')
      })
      .finally(() => {
        this.finishStreamingMessage(
          assistantMessage,
          'La solicitud se cancelo. Puedes volver a intentarlo con una consulta mas concreta si lo necesitas.'
        )
      })
  }

  private scrollToBottom() {
    setTimeout(() => {
      const messages = this.chatMessages?.nativeElement

      if (!messages) {
        return
      }

      messages.scrollTo({
        top: messages.scrollHeight,
        behavior: 'smooth'
      })
    }, 0)
  }

  private startWaitTimer(message: AkerMessage) {
    this.stopWaitTimer()

    const updateElapsedTime = () => {
      message.elapsedSeconds = Math.floor(
        (Date.now() - (message.waitStartedAt || Date.now())) / 1000
      )
      this.messages = [...this.messages]
      this.cdr.detectChanges()
    }

    updateElapsedTime()
    this.waitTimer = setInterval(updateElapsedTime, 1000)
  }

  private stopWaitTimer() {
    if (!this.waitTimer) {
      return
    }

    clearInterval(this.waitTimer)
    this.waitTimer = undefined
  }

  private setThoughtDuration(message: AkerMessage) {
    message.thoughtSeconds =
      message.elapsedSeconds ||
      Math.floor((Date.now() - (message.waitStartedAt || Date.now())) / 1000)
  }

  private resizeTextarea() {
    setTimeout(() => {
      const textarea = this.chatTextarea?.nativeElement

      if (!textarea) {
        return
      }

      textarea.style.height = '0px'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 110)}px`
    }, 0)
  }

  private focusPrompt() {
    setTimeout(() => {
      const textarea = this.chatTextarea?.nativeElement

      if (!textarea || this.isComposerLocked) {
        return
      }

      textarea.focus()
    }, 0)
  }

  private createSessionId() {
    return ''
  }

  private cancelActiveSessionOnExit() {
    if (this.exitCancellationRequested || !this.isSending || !this.sessionId) {
      return
    }

    this.exitCancellationRequested = true
    this.akerService.cancelSessionOnUnload(this.sessionId)
  }

  private createChatId() {
    return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }

  private createMessageId() {
    return `message-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }

  private resetCurrentChat() {
    this.messages = []
    this.prompt = ''
    this.assistantStatus = ''
    this.isLoadingChatMessages = false
    this.chatLoadRequestId++
    this.sessionId = ''
    this.currentChatId = this.createChatId()
    this.heroTitle = this.getRandomHeroMessage()
    this.visibleSuggestions = this.getRandomSuggestions()
    this.currentUserMessage = undefined
    this.currentAssistantMessage = undefined
  }

  private ensureChatHistory(firstMessage: string) {
    let currentChat = this.chatHistory.find(
      (chat) => chat.id === this.currentChatId
    )

    if (currentChat) {
      if (!currentChat.backendChatId) {
        this.persistChat(currentChat)
      }

      return currentChat
    }

    currentChat = {
      id: this.currentChatId,
      title: this.createChatTitle(firstMessage),
      createdAt: new Date(),
      sessionId: this.sessionId,
      messages: []
    }

    this.chatHistory = [currentChat, ...this.chatHistory]
    this.persistChat(currentChat)

    return currentChat
  }

  private updateCurrentChatMessages() {
    this.chatHistory = this.chatHistory.map((chat) =>
      chat.id === this.currentChatId
        ? { ...chat, messages: this.messages }
        : chat
    )
  }

  private updateCurrentChatSession(sessionId: string) {
    this.chatHistory = this.chatHistory.map((chat) =>
      chat.id === this.currentChatId
        ? {
            ...chat,
            externalChatId: sessionId || chat.externalChatId,
            sessionId
          }
        : chat
    )
  }

  private persistCurrentChatWhenSessionIsReady() {
    const chat = this.chatHistory.find((item) => item.id === this.currentChatId)

    if (!chat || chat.backendChatId || !this.sessionId) {
      return
    }

    this.persistChat({
      ...chat,
      externalChatId: this.sessionId,
      sessionId: this.sessionId
    })
  }

  private finishStreamingMessage(
    message: AkerMessage,
    fallbackErrorMessage?: string,
    usage?: AkerMessageUsage
  ) {
    this.setThoughtDuration(message)
    message.isStreaming = false
    message.usage = usage
    this.stopWaitTimer()

    if (fallbackErrorMessage) {
      message.isError = true
      message.content = message.content || fallbackErrorMessage
    }

    this.isSending = false
    this.assistantStatus = ''
    if (this.currentUserMessage && !this.currentUserMessage.backendMessageId) {
      this.persistChatMessage(this.currentUserMessage)
    }
    this.currentUserMessage = undefined
    this.currentAssistantMessage = undefined
    this.streamController = undefined
    this.messages = [...this.messages]
    this.updateCurrentChatMessages()
    this.cdr.detectChanges()
    this.persistChatMessage(message)
    this.scrollToBottom()
    this.focusPrompt()
  }

  private async loadChatHistory() {
    try {
      const chats = await this.akerService.getChats()
      this.chatHistory = chats.map((chat) => this.mapChatRecord(chat))
    } catch (_error) {
      this.chatHistory = []
    }
  }

  private resolveChatMessages(chat: AkerChatSummary) {
    if (chat.messages.length || !chat.backendChatId) {
      return
    }

    const requestId = ++this.chatLoadRequestId
    this.isLoadingChatMessages = true

    this.akerService
      .getChatMessages(chat.backendChatId)
      .then((messages) => {
        if (requestId !== this.chatLoadRequestId) {
          return
        }

        const mappedMessages = messages.map((message) =>
          this.mapChatMessageRecord(message)
        )

        this.chatHistory = this.chatHistory.map((currentChat) =>
          currentChat.id === chat.id
            ? {
                ...currentChat,
                sessionId: this.resolveSessionIdFromMessages(mappedMessages),
                externalChatId:
                  this.resolveSessionIdFromMessages(mappedMessages) ||
                  currentChat.externalChatId,
                messages: mappedMessages
              }
            : currentChat
        )

        if (this.currentChatId === chat.id) {
          this.messages = mappedMessages
          this.sessionId = this.resolveSessionIdFromMessages(mappedMessages)
          this.isLoadingChatMessages = false
          this.scrollToBottom()
        }
      })
      .catch(() => {
        if (requestId === this.chatLoadRequestId) {
          this.isLoadingChatMessages = false
        }
      })
  }

  private persistChat(chat: AkerChatSummary) {
    if (chat.backendChatId || chat.isPersisting) {
      return
    }

    const externalChatId = chat.externalChatId || chat.sessionId

    if (!externalChatId) {
      return
    }

    this.chatHistory = this.chatHistory.map((currentChat) =>
      currentChat.id === chat.id
        ? { ...currentChat, isPersisting: true }
        : currentChat
    )

    this.akerService
      .createChat(externalChatId, chat.title)
      .then((savedChat) => {
        if (!savedChat) {
          return
        }

        this.chatHistory = this.chatHistory.map((currentChat) =>
          currentChat.id === chat.id
            ? {
                ...currentChat,
                backendChatId: savedChat.id,
                externalChatId,
                sessionId: externalChatId,
                isPersisting: false
              }
            : currentChat
        )

        this.persistPendingChatMessages(chat.id)
      })
      .catch(() => {
        this.chatHistory = this.chatHistory.map((currentChat) =>
          currentChat.id === chat.id
            ? { ...currentChat, isPersisting: false }
            : currentChat
        )
      })
  }

  private persistPendingChatMessages(chatId: string) {
    const chat = this.chatHistory.find((item) => item.id === chatId)

    if (!chat?.backendChatId) {
      return
    }

    chat.messages
      .filter((message) => message.content && !message.backendMessageId)
      .forEach((message) => this.persistChatMessageForChat(chat, message))
  }

  private persistChatMessage(message: AkerMessage) {
    const chat = this.chatHistory.find((item) => item.id === this.currentChatId)

    if (!chat) {
      return
    }

    if (!chat.backendChatId) {
      this.persistChat(chat)

      return
    }

    this.persistChatMessageForChat(chat, message)
  }

  private persistChatMessageForChat(
    chat: AkerChatSummary,
    message: AkerMessage
  ) {
    if (!chat.backendChatId || !message.content || message.backendMessageId) {
      return
    }

    const metadata = this.createMessageMetadata(message)

    this.akerService
      .createChatMessage(
        chat.backendChatId,
        message,
        message.role === 'user' ? 'Usuario' : 'AKER',
        metadata
      )
      .then((savedMessage) => {
        if (!savedMessage) {
          return
        }

        message.backendMessageId = savedMessage.id
        this.chatHistory = this.chatHistory.map((currentChat) =>
          currentChat.id === chat.id
            ? {
                ...currentChat,
                messages: currentChat.messages.map((currentMessage) =>
                  currentMessage.id === message.id
                    ? { ...currentMessage, backendMessageId: savedMessage.id }
                    : currentMessage
                )
              }
            : currentChat
        )
      })
      .catch(() => undefined)
  }

  private createMessageMetadata(message: AkerMessage) {
    const hasSessionId = !!this.sessionId

    if (!message.attachments?.length && !message.isError && !hasSessionId) {
      return undefined
    }

    return {
      sessionId: this.sessionId || undefined,
      isError: !!message.isError,
      usage: message.usage,
      attachments: (message.attachments || []).map((attachment) => ({
        name: attachment.name,
        mimeType: attachment.mimeType,
        size: attachment.size
      }))
    }
  }

  private mapChatRecord(chat: AkerChatRecord): AkerChatSummary {
    return {
      id: chat.external_chat_id || `chat-${chat.id}`,
      backendChatId: chat.id,
      externalChatId: chat.external_chat_id,
      title: chat.title || 'Chat sin titulo',
      createdAt: chat.created_at ? new Date(chat.created_at) : new Date(),
      sessionId: chat.external_chat_id || '',
      messages: []
    }
  }

  private mapChatMessageRecord(message: AkerChatMessageRecord): AkerMessage {
    const metadata = this.parseMessageMetadata(message.metadata)

    return {
      id: message.external_message_id || `message-${message.id}`,
      backendMessageId: message.id,
      role: this.mapMessageRole(message.message_type),
      content: message.message,
      createdAt: message.sent_at ? new Date(message.sent_at) : new Date(),
      usage: this.resolveMessageUsage(message) || metadata?.usage || undefined,
      isError: !!metadata?.isError,
      sessionIdHint: metadata?.sessionId
    }
  }

  private mapMessageRole(
    messageType: AkerChatMessageRecord['message_type']
  ): AkerMessage['role'] {
    if (messageType === 'sent') {
      return 'user'
    }

    if (messageType === 'received') {
      return 'assistant'
    }

    return 'system'
  }

  private createChatTitle(message: string) {
    const cleanMessage = message.replace(/\s+/g, ' ').trim()
    const maxLength = 42

    if (cleanMessage.length <= maxLength) {
      return cleanMessage
    }

    return `${cleanMessage.slice(0, maxLength).trim()}...`
  }

  private getFriendlyErrorMessage(error: Error & { code?: string }) {
    const rawMessage = error.message || ''
    const isLongRunningTimeout =
      error.code === 'dependencyFailedException' ||
      error.code === 'timeout' ||
      /model timeout|Dependency resource|tiempo de espera|superado el tiempo/i.test(
        rawMessage
      )

    if (isLongRunningTimeout) {
      return 'AKER ha tardado demasiado en generar la respuesta. Prueba a pedir menos datos o a dividir la consulta en partes mas pequenas.'
    }

    return rawMessage
  }

  private parseMessageMetadata(metadata?: string) {
    if (!metadata) {
      return null
    }

    try {
      return JSON.parse(metadata) as {
        isError?: boolean
        sessionId?: string
        usage?: AkerMessageUsage
      }
    } catch (_error) {
      return null
    }
  }

  private resolveMessageUsage(message: AkerChatMessageRecord) {
    const totalConsumption = this.parseNumericValue(message.total_consumption)
    const inputTokens = this.parseNumericValue(
      message.input_tokens ?? message.prompt_tokens
    )
    const outputTokens = this.parseNumericValue(
      message.output_tokens ?? message.completion_tokens
    )
    const totalTokens = this.parseNumericValue(message.total_tokens)
    const totalCost = this.parseNumericValue(message.total_cost)

    if (
      totalConsumption === undefined &&
      inputTokens === undefined &&
      outputTokens === undefined &&
      totalTokens === undefined &&
      totalCost === undefined
    ) {
      return undefined
    }

    return {
      totalConsumption,
      inputTokens,
      outputTokens,
      totalTokens,
      totalCost
    }
  }

  private parseNumericValue(value: unknown) {
    if (value === undefined || value === null || value === '') {
      return undefined
    }

    const numericValue = Number(value)

    return Number.isFinite(numericValue) ? numericValue : undefined
  }

  private resolveSessionIdFromMessages(messages: AkerMessage[]) {
    for (let index = messages.length - 1; index >= 0; index--) {
      const sessionId = messages[index]?.sessionIdHint

      if (sessionId) {
        return sessionId
      }
    }

    return ''
  }

  private createAttachment(file: AkerStreamFile) {
    const binary = atob(file.data)
    const bytes = new Uint8Array(binary.length)

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index)
    }

    const blob = new Blob([bytes], {
      type: file.mimeType || 'application/octet-stream'
    })

    const objectUrl = URL.createObjectURL(blob)

    return {
      id: this.createMessageId(),
      name: file.name,
      mimeType: file.mimeType || 'application/octet-stream',
      url: this.sanitizer.bypassSecurityTrustUrl(objectUrl),
      objectUrl,
      size: file.size || blob.size
    }
  }

  private revokeAttachmentUrls() {
    this.messages.forEach((message) => {
      message.attachments?.forEach((attachment) => {
        URL.revokeObjectURL(attachment.objectUrl)
      })
    })
  }

  private addExternalLinkAttributes(content: string) {
    return content.replace(/<a\b([^>]*)>/gi, (_match, attributes) => {
      let safeAttributes = attributes

      if (!/\starget=/i.test(safeAttributes)) {
        safeAttributes += ' target="_blank"'
      }

      if (!/\srel=/i.test(safeAttributes)) {
        safeAttributes += ' rel="noopener noreferrer"'
      }

      return `<a${safeAttributes}>`
    })
  }

  private renderMarkdown(content: string) {
    const normalized = this.normalizeMessageContent(content).trim()

    if (!normalized) {
      return ''
    }

    const codeBlocks: string[] = []
    const prepared = normalized.replace(
      /```([\w-]+)?\n([\s\S]*?)```/g,
      (_match, language, code) => {
        const escapedCode = this.escapeHtml(
          String(code || '').replace(/\n$/, '')
        )
        const languageClass = language
          ? ` class="language-${this.escapeHtml(language)}"`
          : ''
        const index =
          codeBlocks.push(
            `<pre class="aker-code-block"><code${languageClass}>${escapedCode}</code></pre>`
          ) - 1

        return `@@CODEBLOCK_${index}@@`
      }
    )

    const lines = prepared.split('\n')
    const html: string[] = []
    let paragraph: string[] = []
    let listType: 'ul' | 'ol' | null = null

    const flushParagraph = () => {
      if (!paragraph.length) {
        return
      }

      html.push(`<p>${this.renderInlineMarkdown(paragraph.join('\n'))}</p>`)
      paragraph = []
    }

    const closeList = () => {
      if (!listType) {
        return
      }

      html.push(`</${listType}>`)
      listType = null
    }

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index]
      const trimmed = line.trim()

      if (!trimmed) {
        flushParagraph()
        closeList()
        continue
      }

      if (/^\|.+\|$/.test(trimmed)) {
        flushParagraph()
        closeList()

        const tableLines = [trimmed]
        while (
          index + 1 < lines.length &&
          /^\|.+\|$/.test(lines[index + 1].trim())
        ) {
          tableLines.push(lines[++index].trim())
        }

        html.push(this.renderMarkdownTable(tableLines))
        continue
      }

      const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/)
      const unorderedMatch = trimmed.match(/^[-*]\s+(.*)$/)

      if (orderedMatch || unorderedMatch) {
        flushParagraph()
        const nextListType = orderedMatch ? 'ol' : 'ul'

        if (listType !== nextListType) {
          closeList()
          listType = nextListType
          html.push(`<${listType}>`)
        }

        const listContent = orderedMatch
          ? orderedMatch[2]
          : unorderedMatch?.[1] || ''
        html.push(`<li>${this.renderInlineMarkdown(listContent)}</li>`)
        continue
      }

      if (/^#{1,6}\s+/.test(trimmed)) {
        flushParagraph()
        closeList()
        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/)
        const level = headingMatch?.[1].length || 2
        const headingContent = headingMatch?.[2] || trimmed
        html.push(
          `<h${level}>${this.renderInlineMarkdown(headingContent)}</h${level}>`
        )
        continue
      }

      paragraph.push(line)
    }

    flushParagraph()
    closeList()

    let rendered = html.join('')
    rendered = rendered.replace(/@@CODEBLOCK_(\d+)@@/g, (_match, rawIndex) => {
      return codeBlocks[Number(rawIndex)] || ''
    })

    return this.addExternalLinkAttributes(rendered)
  }

  private normalizeMessageContent(content: string) {
    const normalized = String(content || '').replace(/\r\n/g, '\n')
    const decoded = this.decodeHtmlEntities(normalized)

    if (!/<\/?[a-z][^>]*>/i.test(decoded)) {
      return decoded
    }

    const parser = new DOMParser()
    const document = parser.parseFromString(decoded, 'text/html')
    const markdown = Array.from(document.body.childNodes)
      .map((node) => this.convertHtmlNodeToMarkdown(node))
      .join('')

    return markdown.replace(/\n{3,}/g, '\n\n')
  }

  private decodeHtmlEntities(value: string) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = value

    return textarea.value
  }

  private convertHtmlChildrenToMarkdown(node: Node) {
    return Array.from(node.childNodes)
      .map((child) => this.convertHtmlNodeToMarkdown(child))
      .join('')
  }

  private convertHtmlNodeToMarkdown(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return ''
    }

    const element = node as HTMLElement
    const tagName = element.tagName.toLowerCase()
    const content = this.convertHtmlChildrenToMarkdown(element)
    const trimmedContent = content.trim()

    switch (tagName) {
      case 'br':
        return '\n'
      case 'p':
      case 'div':
      case 'section':
      case 'article':
        return trimmedContent ? `${trimmedContent}\n\n` : ''
      case 'strong':
      case 'b':
        return trimmedContent ? `**${trimmedContent}**` : ''
      case 'em':
      case 'i':
        return trimmedContent ? `*${trimmedContent}*` : ''
      case 'code':
        return trimmedContent ? `\`${trimmedContent}\`` : ''
      case 'pre': {
        const codeContent = element.textContent?.replace(/\n$/, '') || ''

        return codeContent ? `\n\`\`\`\n${codeContent}\n\`\`\`\n\n` : ''
      }
      case 'a': {
        const href = element.getAttribute('href') || ''
        const label = trimmedContent || href

        return href ? `[${label}](${href})` : label
      }
      case 'ul': {
        const items = Array.from(element.children)
          .filter((child) => child.tagName.toLowerCase() === 'li')
          .map((child) => {
            const itemContent = this.convertHtmlChildrenToMarkdown(child).trim()

            return itemContent ? `- ${itemContent}` : ''
          })
          .filter(Boolean)

        return items.length ? `${items.join('\n')}\n\n` : ''
      }
      case 'ol': {
        const items = Array.from(element.children)
          .filter((child) => child.tagName.toLowerCase() === 'li')
          .map((child, index) => {
            const itemContent = this.convertHtmlChildrenToMarkdown(child).trim()

            return itemContent ? `${index + 1}. ${itemContent}` : ''
          })
          .filter(Boolean)

        return items.length ? `${items.join('\n')}\n\n` : ''
      }
      case 'li':
        return trimmedContent
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        const level = Number(tagName.slice(1))

        return trimmedContent
          ? `${'#'.repeat(level)} ${trimmedContent}\n\n`
          : ''
      }
      case 'blockquote': {
        const quote = trimmedContent
          .split('\n')
          .map((line) => `> ${line}`)
          .join('\n')

        return quote ? `${quote}\n\n` : ''
      }
      default:
        return content
    }
  }

  private renderInlineMarkdown(content: string) {
    let rendered = this.escapeHtml(content)

    rendered = rendered.replace(
      /`([^`]+)`/g,
      (_match, code) => `<code>${this.escapeHtml(code)}</code>`
    )
    rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>')
    rendered = rendered.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    )
    rendered = rendered.replace(
      /(^|[\s>])(https?:\/\/[^\s<>"']+)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>'
    )
    rendered = rendered.replace(/\n/g, '<br />')

    return rendered
  }

  private renderMarkdownTable(lines: string[]) {
    if (lines.length < 2) {
      return `<p>${this.renderInlineMarkdown(lines.join('\n'))}</p>`
    }

    const rows = lines.map((line) =>
      line
        .split('|')
        .slice(1, -1)
        .map((cell) => cell.trim())
    )
    const [header, separator, ...bodyRows] = rows

    const isSeparatorRow = separator?.every((cell) => /^:?-{3,}:?$/.test(cell))
    if (!isSeparatorRow) {
      return `<p>${this.renderInlineMarkdown(lines.join('\n'))}</p>`
    }

    const headerHtml = header
      .map((cell) => `<th>${this.renderInlineMarkdown(cell)}</th>`)
      .join('')
    const bodyHtml = bodyRows
      .map(
        (row) =>
          `<tr>${row
            .map((cell) => `<td>${this.renderInlineMarkdown(cell)}</td>`)
            .join('')}</tr>`
      )
      .join('')

    return `<table class="aker-markdown-table"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`
  }

  private escapeHtml(value: string) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private getRandomHeroMessage() {
    const randomIndex = Math.floor(Math.random() * this.heroMessages.length)

    return this.heroMessages[randomIndex]
  }

  private getRandomSuggestions() {
    return [...this.suggestionPool].sort(() => Math.random() - 0.5).slice(0, 6)
  }

  private getUserName() {
    const storedUserName = localStorage.getItem('userName')

    if (storedUserName) {
      return storedUserName
    }

    const token = this.cookies.get('it')

    if (!token) {
      return ''
    }

    const helper = new JwtHelperService()
    const claims = helper.decodeToken(token)

    return [claims?.name, claims?.given_name, claims?.middle_name]
      .filter(Boolean)
      .join(' ')
  }
}
